import React from 'react';
import { HOME_PATH } from '~/utils/config';
import { handleCheckingSessionID } from '~/utils/checkUrl';
import * as Sentry from '@sentry/react';
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      errorInfo: null,
      sessionID: null,
    };
  }

  componentDidMount() {
    const { history } = this.props;
    const sessionIDLocal = history.location.pathname;
    const getID = handleCheckingSessionID(sessionIDLocal);
    this.setState({ sessionID: getID }, () => {
      this.initSentry();
    });
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    Sentry.withScope((scope) => {
      scope.setExtras(errorInfo);
      if (this.state.sessionID) {
        scope.setTag('sessionId', this.state.sessionID);
      }
      scope.setLevel('error');
      Sentry.captureException(error);
    });
  }

  async initSentry() {
    if (typeof Sentry !== 'undefined') {
      const metaData = await fetch('/meta.json');

      Sentry.init({
        dsn: 'https://dfcb846ab9205e3d708aad00fe1fe458@sentry.system.paveapi.com/14',
        integrations: [
          Sentry.browserTracingIntegration({
            tracingOrigins: [
              'localhost',
              /^.*\.ingest\.sentry\.io$/,
              /^\//, // Same-origin requests
              // Add your application domain and other trusted domains
              process.env.REACT_APP_OPENAPI_ROOT,
              process.env.REACT_APP_PAVE_FORMS,
              process.env.REACT_APP_PAVE_VEHICLES_API,
              process.env.REACT_APP_PAVE_DASHBOARD,
              process.env.REACT_APP_PAVE_CAGES_CDN,
              process.env.REACT_APP_BASE_URL,
            ],
          }),
          Sentry.browserProfilingIntegration(),
        ],
        // Performance Monitoring
        tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.2 : 0.8,
        // Session Replay
        replaysSessionSampleRate: 0.05, // Sample 5% of sessions
        replaysOnErrorSampleRate: 0.3, // Sample 50% of sessions with errors

        beforeSend(event, hint) {
          const error = hint?.originalException;
          if (error && error.name === 'ChunkLoadError') {
            return null; // Don't send chunk load errors to Sentry
          }
          return event;
        },
        // Error Tracking
        environment: process.env.NODE_ENV,
        release: metaData?.version || '1.0.0',

        ignoreErrors: [
          'InvalidAccessError: Track has ended',
          'UnknownError: setPhotoOptions failed',
          // Network errors
          'Network request failed',
          'Failed to fetch',
          // Browser extension errors
          /^chrome-extension:\/\//,
          /^moz-extension:\/\//,
          // Common React errors that we can't control
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications',
          // Ignore cancelled requests
          /^Aborted$/,
          /^AbortError$/,
        ],
      });

      // Set global tags
      Sentry.setTag(' ', process.env.NODE_ENV);
      if (this.state.sessionID) {
        Sentry.setTag('sessionId', this.state.sessionID);
      }
    }
  }

  handleReportError = () => {
    const { error, errorInfo, sessionID } = this.state;
    if (error) {
      Sentry.withScope((scope) => {
        scope.setExtras(errorInfo);
        scope.setTag('source', 'user_reported');
        scope.setLevel('error');
        if (sessionID) {
          scope.setTag('sessionId', sessionID);
        }
        Sentry.captureException(error);
      });
    }
  };

  render() {
    const { error, errorInfo, sessionID } = this.state;
    const { history } = this.props;

    if (errorInfo) {
      return (
        <div
          style={{
            background: '#fff',
            height: 'calc(100vh)',
            padding: '10px',
            textAlign: 'center',
          }}
        >
          <section style={{ padding: '10px' }}>
            <span>
              <i
                style={{ fontSize: '100px', opacity: '.7' }}
                className="fas fa-tools"
              />
            </span>
          </section>
          <section>
            <h1>Friendly Error</h1>
            <p>
              Something went wrong. Our team has been notified and we're working
              on it.
            </p>
          </section>
          <section>
            <details style={{ whiteSpace: 'pre-wrap' }}>
              {error && error.toString()}
              <br />
              {errorInfo.componentStack}
            </details>
          </section>
          <section style={{ padding: '15px' }}>
            <button
              style={{ marginRight: '15px' }}
              onClick={
                !!sessionID
                  ? () => {
                      history.push(`/${HOME_PATH}/${sessionID}/stage=0`);
                      window.location.reload();
                    }
                  : () => {
                      window.location.reload();
                    }
              }
            >
              {!!sessionID ? 'Back Home' : 'Reload'}
            </button>
            <button onClick={this.handleReportError}>Report Error</button>
          </section>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
